import {
  DOCUMENT_VIEW_CATEGORY_OTHER,
  DOCUMENT_VIEW_CATEGORY_STATEMENTS,
  DOCUMENT_VIEW_CATEGORY_TAX,
  DOCUMENT_VIEW_SELECTION_CONFIG_PREFIX,
} from "../constants/StatementsDocuments.constants";

const EMPTY_SELECTIONS = () => ({
  [DOCUMENT_VIEW_CATEGORY_STATEMENTS]: [],
  [DOCUMENT_VIEW_CATEGORY_TAX]: [],
  [DOCUMENT_VIEW_CATEGORY_OTHER]: [],
});

/** Returns the stable key stored in config for an option: schemaId when available, else label string. */
const getOptionKey = (option) =>
  typeof option === "string" ? option : (option?.schemaId || option?.label || "");

/**
 * Serialises the current selections to the config string stored in
 * DOCUMENTS_ALLOWED_TO_BE_DOWNLOADED.
 *
 * Each selected schema is stored as `schemaId:displayText` when a matching option
 * (with a displayText / label) is found in optionsByCategory, so the backend can
 * resolve a human-readable name without calling XDoc document-types at runtime.
 * Plain schemaIds (no colon) are emitted for any key that has no option data.
 *
 * @param {Object} selections        – { [category]: string[] } of selected schemaIds
 * @param {Object} optionsByCategory – { [category]: option[] } full option objects
 */
export function serializeDocumentViewSelections(selections, optionsByCategory = {}) {
  return [
    DOCUMENT_VIEW_CATEGORY_STATEMENTS,
    DOCUMENT_VIEW_CATEGORY_TAX,
    DOCUMENT_VIEW_CATEGORY_OTHER,
  ]
    .map((category) => {
      const prefix = DOCUMENT_VIEW_SELECTION_CONFIG_PREFIX[category];
      const options = optionsByCategory[category] ?? [];
      const values = (selections[category] ?? []).map((key) => {
        const option = options.find(
          (o) => (typeof o === "string" ? o : (o?.schemaId || o?.label || "")) === key,
        );
        const displayText =
          option && typeof option !== "string"
            ? (option.displayText ?? option.label ?? null)
            : null;
        return displayText ? `${key}:${displayText}` : key;
      });
      return `${prefix}=${values.join(";")}`;
    })
    .join("|");
}

export function parseDocumentViewSelections(value) {
  if (!value?.length) {
    return null;
  }


  const selections = EMPTY_SELECTIONS();
  value.split("|").forEach((segment) => {
    const [prefix, rawValues] = segment.split("=");
    // Strip :displayText suffix — schemaId is always the part before the first ':'
    const values = rawValues
      ? rawValues.split(";").filter(Boolean).map((v) => {
          const colon = v.indexOf(":");
          return colon >= 0 ? v.substring(0, colon).trim() : v.trim();
        })
      : [];
    if (
      prefix ===
      DOCUMENT_VIEW_SELECTION_CONFIG_PREFIX[DOCUMENT_VIEW_CATEGORY_STATEMENTS]
    ) {
      selections[DOCUMENT_VIEW_CATEGORY_STATEMENTS] = values;
    }
    if (
      prefix === DOCUMENT_VIEW_SELECTION_CONFIG_PREFIX[DOCUMENT_VIEW_CATEGORY_TAX]
    ) {
      selections[DOCUMENT_VIEW_CATEGORY_TAX] = values;
    }
    if (
      prefix === DOCUMENT_VIEW_SELECTION_CONFIG_PREFIX[DOCUMENT_VIEW_CATEGORY_OTHER]
    ) {
      selections[DOCUMENT_VIEW_CATEGORY_OTHER] = values;
    }
  });

  return selections;
}

export function resolveInitialDocumentViewSelections(
  configuredValue,
  optionsByCategory,
) {
  const parsed = parseDocumentViewSelections(configuredValue);
  const selections = EMPTY_SELECTIONS();


  if (parsed) {
    Object.keys(selections).forEach((category) => {
      const availableKeys = (optionsByCategory[category] ?? []).map(getOptionKey);
      selections[category] = (parsed[category] ?? []).filter((key) =>
        availableKeys.includes(key),
      );
    });
    return selections;
  }

  return selections;
}

/**
 * Which of the three document pages have anything to show. A client that has never selected
 * anything shows all three, so no client loses access to documents it never configured.
 */
export function getVisibleDocumentViewCategories(configValue) {
  const parsed = parseDocumentViewSelections(configValue);

  if (!parsed) {
    return {
      [DOCUMENT_VIEW_CATEGORY_STATEMENTS]: true,
      [DOCUMENT_VIEW_CATEGORY_TAX]: true,
      [DOCUMENT_VIEW_CATEGORY_OTHER]: true,
    };
  }

  return {
    [DOCUMENT_VIEW_CATEGORY_STATEMENTS]:
      (parsed[DOCUMENT_VIEW_CATEGORY_STATEMENTS] ?? []).length > 0,
    [DOCUMENT_VIEW_CATEGORY_TAX]:
      (parsed[DOCUMENT_VIEW_CATEGORY_TAX] ?? []).length > 0,
    [DOCUMENT_VIEW_CATEGORY_OTHER]:
      (parsed[DOCUMENT_VIEW_CATEGORY_OTHER] ?? []).length > 0,
  };
}
